import { SECTION_MAX_WIDTH } from "../constants";
import { AutoAdaptUtils, ComponentIds, HierarchyUtils, SelectedComponent, SnapUtils, Transform, TransformUtils, Vector, } from "@inter-ikea-core/core";
import { CANT_CREATE_ENTITY, CHILD_IS_ORPHAN, CONNECTOR_DOESNT_EXISTS, ENTITY_NULL, NO_CONNECTION, NO_PARENT, NO_PRODUCT_REF, NO_SIZE, NO_SNAP_ACTION, } from "@inter-ikea-storagetwo/range/dist/constants/errors";
import { NOT_A_MOUNTING_RAIL, UPRIGHT_NOT_CORRECTLY_REATTACHED, } from "../constants/errors";
import { COMPONENT_IDS } from "@inter-ikea-storagetwo/range/dist/storage2ComponentIDs";
import { getWidthOfEntity } from "../utils/getEntityData";
import { getUprights } from "../utils/sectionsHelpers";
import { MOUNTING_RAIL_ART_NUMBERS } from "../constants/connectorKeys";
import { getRightUprightsOffsetFromLeft } from "./interactiveBoaxelSection";
import { MountingRailConnectionService, } from "../utils/ConnectorService";
import { LEFT } from "@inter-ikea-storagetwo/range/dist/constants";
import { getConnectorsComponent, getHostConnectionComponent, } from "@inter-ikea-storagetwo/range/dist/functions/componentGetters";
import MountingRailComponent from "../components/MountingRailComponent";
import RequestDestructionComponent from "@inter-ikea-storagetwo/range/dist/components/RequestDestructionComponent";
/**
 * Get mounting rails
 */
export const getMountingRails = (core) => HierarchyUtils.getDescendantsWithComponent(core.services.sceneManager.scene, COMPONENT_IDS.MOUNTING_RAIL_COMPONENT);
/**
 * Finds an entity in the given array whose width matches the specified width.
 *
 * @param {number} width - The width to match against.
 * @param {Entity[]} entities - The array of entities to search for a matching width.
 * @returns {Entity|undefined} - The entity with the matching width, or undefined if not found.
 */
export const findMatchingWidthEntity = (width, entities) => {
    return entities.find((entity) => {
        if (entity.hasComponent(COMPONENT_IDS.NON_WORLDLY_ITEM_COMPONENT))
            return false;
        const adjustableComponent = entity.getComponent(COMPONENT_IDS.ADJUSTABLE_COMPONENT);
        if (adjustableComponent)
            return (adjustableComponent.min <= width && adjustableComponent.max >= width);
        const entityWidth = getWidthOfEntity(entity);
        if (!entityWidth)
            throw new Error(NO_SIZE);
        return entityWidth === width;
    });
};
/**
 * Returns the left or right upright of a mounting rail entity.
 *
 * @param {Entity} mountingRail The mounting rail entity to get the upright from.
 * @param {typeof LEFT | typeof RIGHT} [side=LEFT] The side from which to get the upright (LEFT or RIGHT).
 * @returns {Upright | undefined} The left or right upright of the mounting rail entity, or undefined if not found.
 */
export const getUprightBySide = (mountingRail, side) => {
    const uprights = getUprights(mountingRail);
    return side === LEFT ? uprights === null || uprights === void 0 ? void 0 : uprights.leftUpright : uprights === null || uprights === void 0 ? void 0 : uprights.rightUpright;
};
/**
 * Retrieves the product reference from the specified entity.
 *
 * @param {Entity} entity - The entity to retrieve the product reference from.
 * @param {boolean} [needResult=false] - Indicates if the result is required, defaults to false.
 * @returns {ProductRef | undefined} The product reference if found, undefined otherwise.
 * @throws {Error} - Throws an error if needResult is true but no product reference is found.
 */
export const getEntityProductRef = (entity, needResult = false) => {
    var _a;
    const res = (_a = entity.getComponent(ComponentIds.PRODUCT_COMPONENT_ID)) === null || _a === void 0 ? void 0 : _a.productRef;
    if (needResult && !res)
        throw new Error(NO_PRODUCT_REF);
    return res;
};
/**
 * Gets mounting rail article number by connector key
 * @param hostConnector
 */
export const getMountingRailArtNmbrByConnectorKey = ({ hostConnector,
// @ts-ignore
 }) => MOUNTING_RAIL_ART_NUMBERS[hostConnector.key];
export const severChildConnectionToParent = (connection) => {
    connection.guestConnectorEntity.removeComponent(ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID);
    return connection;
};
/**
 * Removes all children connection to its parent and returns an object containing the connection data
 * @param childrenConnections
 */
export const severChildrenConnectionToParent = (childrenConnections) => {
    return childrenConnections.reduce((connections, connection) => [
        ...connections,
        severChildConnectionToParent(connection),
    ], []);
};
/**
 * Creates a valid snap action or throws an error
 * @param core
 * @param guest
 * @param guestConnector
 * @param host
 * @param hostConnector
 */
export const createSnapAction = (core, guest, guestConnector, host, hostConnector) => {
    const snapActionCandidate = SnapUtils.getSnap(guest, guestConnector, host, hostConnector);
    if (!snapActionCandidate)
        throw new Error(NO_SNAP_ACTION);
    return core.services.validations.validateSnapCandidate(snapActionCandidate);
};
/**
 * Creates several snap actions based on array on entities to a given entity
 * @param core
 * @param entity
 * @param connections
 */
const createSnapActions = (core, entity, connections) => connections.map((connection) => {
    const { hostConnectorKey, guestConnectorKey, guestConnectorEntity } = connection;
    const hostConnector = getConnectorsComponent(entity).getConnectorByKey(hostConnectorKey);
    const guestConnector = getConnectorsComponent(guestConnectorEntity).getConnectorByKey(guestConnectorKey);
    if (!hostConnector || !guestConnector)
        throw new Error(CONNECTOR_DOESNT_EXISTS);
    return createSnapAction(core, guestConnectorEntity, guestConnector, entity, hostConnector);
});
/**
 * Takes an entity and severed connections and snaps them toghether
 *
 * @param core
 * @param entity
 * @param children
 */
export const performSnap = (core, entity, children) => {
    const entitySnapActionCandidates = createSnapActions(core, entity, children);
    entitySnapActionCandidates.forEach(({ errors, snap, valid }) => {
        if (errors.length)
            console.error(errors, snap, valid, entity);
        if (!errors.length && valid)
            snap === null || snap === void 0 ? void 0 : snap.guest.removeComponent(ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID);
        snap.performMoveAndConnect();
    });
};
/**
 * Replace an entity en reapplies its children.
 *
 * @param core
 * @param oldEntity
 * @param newEntity
 * @param children
 * @param maintainPosition
 */
export const replaceEntity = (core, oldEntity, newEntity, children = [], maintainPosition = true) => {
    if (maintainPosition)
        copyPositionAndApplyFromOtherEntity(oldEntity, newEntity);
    const parent = HierarchyUtils.parent(oldEntity);
    if (!parent)
        throw new Error(CHILD_IS_ORPHAN);
    const entityToParentConnection = getHostConnectionComponent(parent).connections.find((con) => con.guestConnectorEntity.id === oldEntity.id);
    const severedEntityToParentConnection = severChildrenConnectionToParent([
        entityToParentConnection,
    ]).map((severedConnection) => (Object.assign(Object.assign({}, severedConnection), { guestConnectorEntity: newEntity })));
    // Snap replace entity to original parent
    performSnap(core, parent, severedEntityToParentConnection);
    // Snap children to replaced entity
    performSnap(core, newEntity, children);
    oldEntity.dispose();
};
/**
 * Copies a position from an entity and applies it to a new one. Also
 * accounts for origin width, so that the new entity aligns on the left end
 * with original entity.
 *
 * @param originEntity
 * @param entity
 */
export const copyPositionAndApplyFromOtherEntity = (originEntity, entity) => {
    const { position: { X, Y, Z }, rotation, } = TransformUtils.worldTransform(originEntity);
    const leftCorrection = (getWidthOfEntity(originEntity) - getWidthOfEntity(entity)) / 2;
    const newPosition = new Vector(X - leftCorrection, Y, Z);
    const newTransform = new Transform(newPosition, rotation);
    TransformUtils.setWorldTransform(entity, newTransform);
};
/**
 * Replaces a mounting rail with a smaller och larger one, depending on which
 * connector its upright snapped to.
 * @param core
 * @param connection
 */
export const resizeChildren = (core, connection) => {
    const { hostConnectorEntity, guestConnectorEntity } = connection;
    const width = getRightUprightsOffsetFromLeft(hostConnectorEntity, guestConnectorEntity);
    const leftUpright = getUprightBySide(hostConnectorEntity, LEFT);
    if (!leftUpright)
        throw new Error(UPRIGHT_NOT_CORRECTLY_REATTACHED);
    autoAdaptChildren(leftUpright, width, core);
};
/**
 * Automatically adapts children of a given entity based on the specified parameters.
 *
 * @param {Entity} upright The entity whose children need to be adapted.
 * @param {number} mountingRailWidth The width for mounting rail.
 * @param {Core} _core The core object for processing.
 */
export const autoAdaptChildren = (upright, mountingRailWidth, _core) => {
    const uprightChildren = HierarchyUtils.children(upright).filter((child) => child.hasComponent(COMPONENT_IDS.INSERT));
    if (!(uprightChildren === null || uprightChildren === void 0 ? void 0 : uprightChildren.length))
        return;
    uprightChildren.forEach(autoAdaptEntity(_core, mountingRailWidth));
};
/**
 * Removes draggable component on an entities children
 *
 * @param entity
 */
export const disableDraggableOnChildren = (entity) => {
    const root = getRootMountingRail(entity);
    const descendants = HierarchyUtils.descendants(root);
    descendants.forEach((entity) => entity.removeComponent(ComponentIds.DRAGGABLE_COMPONENT_ID));
};
/**
 * Takes an entity and steps upwards in its parent hierarchy to find the root mountingRail
 * @param entity
 */
export const getRootMountingRail = (entity) => {
    const parentEntity = HierarchyUtils.parent(entity);
    if (entity.hasComponent(COMPONENT_IDS.MOUNTING_RAIL_COMPONENT) &&
        (!parentEntity ||
            !parentEntity.hasComponent(COMPONENT_IDS.UPRIGHT_COMPONENT))) {
        return entity;
    }
    if (!parentEntity)
        throw new Error(NO_PARENT);
    return getRootMountingRail(parentEntity);
};
/**
 * Automatically adapts the provided entity based on the mounting rail width, using the Core.
 *
 * @param {number} mountingRailWidth - The width of the mounting rail.
 * @param {Core} _core - The Core instance.
 * @returns {function(Entity): void} A function that adapt the entity based on the mounting rail width.
 */
export const autoAdaptEntity = (core, mountingRailWidth) => {
    return (entity) => {
        var _a;
        const connectionService = MountingRailConnectionService(core);
        const productRef = (_a = entity.getComponent(ComponentIds.PRODUCT_COMPONENT_ID)) === null || _a === void 0 ? void 0 : _a.productRef;
        if (!productRef)
            throw new Error(NO_PRODUCT_REF);
        const variants = AutoAdaptUtils.getAutoAdaptVariants(entity);
        const newChild = findMatchingWidthEntity(mountingRailWidth, variants);
        if (!newChild)
            return entity.dispose();
        const newChildProductRef = getEntityProductRef(newChild, true);
        // Check if child has own children that need auto adapting, e.g. shelf with clothes rail attached.
        const ownChildrenConnections = HierarchyUtils.children(entity)
            .filter((e) => e.hasComponent(ComponentIds.AUTO_ADAPT_GUEST_COMPONENT_ID))
            .map((child) => connectionService.disconnect(child));
        const newEntity = connectionService
            .replace(entity)
            .with(newChildProductRef);
        return ownChildrenConnections.forEach((connection) => {
            if (!connection)
                throw new Error(NO_CONNECTION);
            const childVariants = AutoAdaptUtils.getAutoAdaptVariants(connection === null || connection === void 0 ? void 0 : connection.guestConnectorEntity);
            const newChild = findMatchingWidthEntity(mountingRailWidth, childVariants);
            if (!newChild || !newEntity)
                throw new Error(ENTITY_NULL);
            return connectionService
                .connect(newChild)
                .to(newEntity)
                .atConnector(connection === null || connection === void 0 ? void 0 : connection.hostConnector.key);
        });
    };
};
/**
 * Creates an entity and makes sure it exists
 * @param _productRef
 * @param _productManager
 */
export const createEntity = (_productRef, _productManager) => {
    const newEntity = _productManager.createEntity(_productRef);
    if (!newEntity)
        throw new Error(CANT_CREATE_ENTITY);
    return newEntity;
};
/**
 * Event handler for when a user click in between two uprights
 * @param mountingRail
 */
export const onClickInvisibleZone = (mountingRail) => () => mountingRail.addComponent(new SelectedComponent());
export const resizeMountingRailModel = (mountingRail, rightUpright, viewerObject, viewer) => {
    const viewerObjectId = viewerObject === null || viewerObject === void 0 ? void 0 : viewerObject.getViewerObjectId();
    const viewerObjectChildId = viewer.get_children(viewerObjectId)[0];
    const width = getRightUprightsOffsetFromLeft(mountingRail, rightUpright);
    viewer.set_position(viewerObjectChildId, -(SECTION_MAX_WIDTH - width) / 2 / 1000, 0, 0);
    viewer.set_rotation(viewerObjectChildId, 0, 0, 0);
    viewer.set_scale(viewerObjectChildId, width / SECTION_MAX_WIDTH, 1, 1);
};
/**
 * Handle boaxel hierarchy: Use RequestDestructionComponent to remove entites in order.
 * @param mountingRail
 */
export const removeSection = (mountingRail) => {
    if (!mountingRail.hasComponent(MountingRailComponent.id))
        throw new Error(NOT_A_MOUNTING_RAIL);
    mountingRail.addComponent(new RequestDestructionComponent());
};
